// Blueprint library (requirement 2).
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import ButtonDropdown from '@cloudscape-design/components/button-dropdown';
import Header from '@cloudscape-design/components/header';
import Input from '@cloudscape-design/components/input';
import Link from '@cloudscape-design/components/link';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Table from '@cloudscape-design/components/table';
import TextFilter from '@cloudscape-design/components/text-filter';
import Toggle from '@cloudscape-design/components/toggle';
import { api } from '../../api/client';
import { useBlueprints } from '../../api/hooks';
import { formatDate } from '../../lib/format';
import { errorMessage, useNotify } from '../../lib/notify';

export function LibraryPage() {
  const [q, setQ] = useState('');
  const [docType, setDocType] = useState('');
  const [archived, setArchived] = useState(false);
  const navigate = useNavigate();
  const notify = useNotify();
  const qc = useQueryClient();
  const query = useBlueprints({
    q: q || undefined,
    docType: docType || undefined,
    archived: archived ? 'true' : 'false',
  });
  const items = query.data?.items ?? [];

  const act = async (id: string, action: string) => {
    try {
      if (action === 'edit') return navigate(`/blueprints/${id}/edit`);
      if (action === 'run') return navigate(`/run?blueprintId=${id}`);
      if (action === 'duplicate') {
        const copy = await api.duplicate(id);
        notify.success(`Created "${copy.name}".`);
      }
      if (action === 'archive' || action === 'unarchive') {
        await api.archive(id, action === 'archive');
        notify.success(action === 'archive' ? 'Blueprint archived.' : 'Blueprint restored.');
      }
      await qc.invalidateQueries({ queryKey: ['blueprints'] });
    } catch (e) {
      notify.error(errorMessage(e));
    }
  };

  return (
    <Table
      header={
        <Header
          variant="h1"
          counter={query.data ? `(${items.length}${query.data.nextCursor ? '+' : ''})` : undefined}
          actions={
            <Button variant="primary" onClick={() => navigate('/blueprints/new')}>
              Create blueprint
            </Button>
          }
        >
          Blueprints
        </Header>
      }
      loading={query.isLoading}
      loadingText="Loading blueprints"
      items={items}
      trackBy="id"
      filter={
        <SpaceBetween direction="horizontal" size="m" alignItems="center">
          <TextFilter
            filteringText={q}
            filteringPlaceholder="Search name or description"
            filteringAriaLabel="Search blueprints"
            onChange={(e) => setQ(e.detail.filteringText)}
          />
          <Input
            value={docType}
            placeholder="Document type"
            ariaLabel="Filter by document type"
            onChange={(e) => setDocType(e.detail.value)}
          />
          <Toggle checked={archived} onChange={(e) => setArchived(e.detail.checked)}>
            Archived
          </Toggle>
        </SpaceBetween>
      }
      empty={
        <Box textAlign="center" color="inherit">
          <b>{archived ? 'No archived blueprints' : 'No blueprints yet'}</b>
          {!archived && (
            <Box padding={{ top: 's' }}>
              <Button onClick={() => navigate('/blueprints/new')}>Create blueprint</Button>
            </Box>
          )}
        </Box>
      }
      columnDefinitions={[
        {
          id: 'name',
          header: 'Name',
          cell: (b) => (
            <Link
              href={`/blueprints/${b.id}`}
              onFollow={(e) => {
                e.preventDefault();
                navigate(`/blueprints/${b.id}`);
              }}
            >
              {b.name}
            </Link>
          ),
          isRowHeader: true,
        },
        { id: 'type', header: 'Document type', cell: (b) => b.documentType },
        { id: 'description', header: 'Description', cell: (b) => b.description || '-' },
        {
          id: 'version',
          header: 'Latest version',
          cell: (b) => (b.latestVersion ? `v${b.latestVersion}` : 'Draft only'),
        },
        { id: 'updated', header: 'Last modified', cell: (b) => formatDate(b.updatedAt) },
        { id: 'runs', header: 'Runs', cell: (b) => b.runCount },
        {
          id: 'actions',
          header: 'Actions',
          cell: (b) => (
            <ButtonDropdown
              variant="inline-icon"
              ariaLabel={`Actions for ${b.name}`}
              items={[
                { id: 'edit', text: 'Edit' },
                { id: 'run', text: 'Run a document' },
                { id: 'duplicate', text: 'Duplicate' },
                b.archived ? { id: 'unarchive', text: 'Restore from archive' } : { id: 'archive', text: 'Archive' },
              ]}
              onItemClick={(e) => void act(b.id, e.detail.id)}
            />
          ),
        },
      ]}
    />
  );
}
